import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subscription, catchError, finalize, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PushLiveService } from '@core/services/push-live/push-live.service';
import { PushLiveChapter } from '@core/models/push-live-chapter.model';
import { UserParticipation } from '@core/models/user-participation.response.model';
import { ParticipationService } from '@core/services/participation/participation.service';
import { Chapter } from '@core/models/chapter.model';
import { ChapterItem } from '@core/models/chapter-item.model';

@Component({
  selector: 'app-push-live-sidebar',
  templateUrl: './push-live-sidebar.component.html',
  styleUrl: './push-live-sidebar.component.css',
})
export class PushLiveSidebarComponent implements OnInit, OnDestroy {
  livePushChapters$ = new BehaviorSubject<PushLiveChapter | null>(null);
  userParticipation$ = new BehaviorSubject<UserParticipation>({} as UserParticipation);
  loading = false;
  isCollapsed = false;
  errorMessage = '';
  pushLiveUuid: string | null = null;
  currentItemUuid: string | null = null;
  private subscriptions = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly pushLiveService: PushLiveService,
    private readonly participationService: ParticipationService
  ) {}

  ngOnInit(): void {
    if (window.innerWidth <= 768) {
      this.isCollapsed = true;
    }
    this.initializeCourseUuid();
    this.trackParamChanges();
  }

  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth > 768 && this.isCollapsed) {
      this.isCollapsed = false;
    }
  }

  private initializeCourseUuid(): void {
    let activeRoute = this.route.root;

    while (activeRoute?.firstChild) {
      activeRoute = activeRoute.firstChild;
    }

    const routeSub = activeRoute.paramMap.subscribe((paramMap) => {
      this.pushLiveUuid = paramMap.get('uuid');
      this.currentItemUuid = paramMap.get('questionSet') || paramMap.get('lesson') || null;
      if (this.pushLiveUuid) {
        this.fetchPushLiveChapters(this.pushLiveUuid);
        this.getUserParticipation(this.pushLiveUuid);
      }
    });

    this.subscriptions.add(routeSub);
  }

  private fetchPushLiveChapters(pushLiveUuid: string): void {
    this.loading = true;
    const chapterSub = this.pushLiveService
      .getPushLiveChapters(pushLiveUuid)
      .pipe(
        catchError((error) => {
          this.errorMessage = error.message;
          return of(null);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe((data) => {
        if (data) {
          this.livePushChapters$.next(data);
        }
      });

    this.subscriptions.add(chapterSub);
  }

  private trackParamChanges(): void {
    const routeChangeSub = this.router.events
      .pipe(filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        let activeRoute = this.route.root;
        while (activeRoute?.firstChild) {
          activeRoute = activeRoute.firstChild;
        }

        const paramMap = activeRoute.snapshot.paramMap;
        const newUuid = paramMap.get('uuid');
        this.currentItemUuid = paramMap.get('questionSet') || paramMap.get('lesson') || null;
        this.getUserParticipation(newUuid!);
        this.fetchPushLiveChapters(newUuid!);
      });

    this.subscriptions.add(routeChangeSub);
  }

  private getUserParticipation(pushLiveUuid: string): void {
    this.loading = true;

    const participationRouteSub = this.participationService
      .getPushLiveUserParticipationDetails(pushLiveUuid)
      .pipe(
        catchError((error) => {
          this.errorMessage = error.message;
          return of(null);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe((response) => {
        if (response?.data) {
          this.userParticipation$.next(response.data);
        }
      });

    this.subscriptions.add(participationRouteSub);
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  isChapterCompleted(chapter: Chapter): boolean {
    return chapter.chapter_items.every((item: ChapterItem) => item.content.user_progress === 100);
  }

  isChapterCurrent(chapter: Chapter): boolean {
    return chapter.chapter_items.some(
      (item: ChapterItem) => item.content.uuid === this.currentItemUuid
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
